import React, { FC, useState, useEffect } from "react";
import Avatar from "shared/Avatar/Avatar";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import Label from "components/Label/Label";
import CommonLayout from "./CommonLayout";
import { useAuth } from "components/AuthLogin/AuthContext";
import SuccessModal from 'containers/BlogPage/BlogModal/SuccessModal';

export interface AccountPageProps {
  className?: string;
}

const AccountPage: FC<AccountPageProps> = ({ className = "" }) => {
  const { getCookie, user, refreshUser } = useAuth();
  const [userData, setUserData] = useState({
    avatar: user?.avatar || "",
    desc: user?.desc || "",
    displayName: user?.displayName || "",
    email: user?.email || "",
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    name: user?.name || "",
  });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (user) {
      setUserData({
        avatar: user.avatar || "",
        desc: user.desc || "",
        displayName: user.displayName || "",
        email: user.email || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        name: user.name || "",
      });
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setUserData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  useEffect(() => {
    if (!avatarFile) return;
    const url = URL.createObjectURL(avatarFile);
    setAvatarPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [avatarFile]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAvatarFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const apiToken = getCookie('apiToken');
    if (apiToken) {
      setIsSaving(true);
      const formData = new FormData();
      formData.append('name', userData.name || '');
      formData.append('displayName', userData.displayName || '');
      formData.append('email', userData.email || '');
      formData.append('desc', userData.desc || '');

      if (avatarFile) {
        formData.append('avatar', avatarFile);
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/info`, {
          method: "POST",
          headers: { 'Authorization': `Bearer ${apiToken}` },
          body: formData,
        });

        if (!response.ok) {
          throw new Error("Failed to update user data");
        }

        const updatedData = await response.json();
        setUserData(updatedData);
        await refreshUser();
        setIsSuccessModalOpen(true);
      } catch (error) {
        console.error("Error updating user data:", error);
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <div className={`nc-AccountPage ${className}`} data-nc-id="AccountPage">
      <CommonLayout>
        <div className="space-y-6 sm:space-y-8">
          {/* HEADING */}
          <h2 className="text-3xl font-semibold">계정 정보</h2>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col md:flex-row">
              <div className="flex-shrink-0 flex items-start">
                <div className="relative rounded-full overflow-hidden flex">
                  <Avatar sizeClass="w-32 h-32" imgUrl={avatarPreview || userData.avatar}/>
                  <div
                      className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center text-neutral-50 cursor-pointer">
                    <svg
                        width="30"
                        height="30"
                        viewBox="0 0 30 30"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                          d="M17.5 5H7.5C6.83696 5 6.20107 5.26339 5.73223 5.73223C5.26339 6.20107 5 6.83696 5 7.5V20M5 20V22.5C5 23.163 5.26339 23.7989 5.73223 24.2678C6.20107 24.7366 6.83696 25 7.5 25H22.5C23.163 25 23.7989 24.7366 24.2678 24.2678C24.7366 23.7989 25 23.163 25 22.5V17.5M5 20L10.7325 14.2675C11.2013 13.7988 11.8371 13.5355 12.5 13.5355C13.1629 13.5355 13.7987 13.7988 14.2675 14.2675L17.5 17.5M25 12.5V17.5M25 17.5L23.0175 15.5175C22.5487 15.0488 21.9129 14.7855 21.25 14.7855C20.5871 14.7855 19.9513 15.0488 19.4825 15.5175L17.5 17.5M17.5 17.5L20 20M22.5 5H27.5M25 2.5V7.5M17.5 10H17.5125"
                          stroke="currentColor"
                          strokeWidth={1.5}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                      />
                    </svg>

                    <span className="mt-1 text-xs">이미지 변경</span>
                  </div>
                  <input
                      type="file"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={handleAvatarChange}
                  />
                </div>
              </div>
              <div className="flex-grow mt-10 md:mt-0 md:pl-16 max-w-3xl space-y-6">
                <div>
                  <Label>이름</Label>
                  <Input
                      className="mt-1.5"
                      name="name"
                      value={userData.name}
                      onChange={handleInputChange}
                  />
                </div>
                <div>
                  <Label>닉네임</Label>
                  <Input
                      className="mt-1.5"
                      name="displayName"
                      value={userData.displayName}
                      onChange={handleInputChange}
                  />
                </div>
                <div>
                  <Label>이메일</Label>
                  <Input
                      className="mt-1.5"
                      name="email"
                      value={userData.email}
                      onChange={handleInputChange}
                  />
                </div>
                <div>
                  <Label>한 줄 소개</Label>
                  <Textarea
                      className="mt-1.5"
                      name="desc"
                      value={userData.desc}
                      onChange={handleInputChange}
                  />
                </div>
                <div className="pt-2">
                  <ButtonPrimary type="submit" disabled={isSaving}>
                    {isSaving ? "저장 중..." : "저장하기"}
                  </ButtonPrimary>
                </div>
              </div>
            </div>
          </form>
        </div>
      </CommonLayout>
      <SuccessModal
          isOpen={isSuccessModalOpen}
          onClose={() => setIsSuccessModalOpen(false)}
          title="저장 완료"
          mainMessage="프로필이 저장됐어요."
          subMessage="변경사항이 저장됐어요."
      />
    </div>
  );
};

export default AccountPage;
